import React, { useEffect, useMemo, useState } from 'react';

/**
 * Reusable client-side pagination for tables.
 *
 *  const { paged, controls } = usePagination(filteredList);
 *  ...
 *  <Table>...{paged.map(...)}</Table>
 *  {controls}
 *
 * The hook returns:
 *   paged        — the items for the current page (use this in your map)
 *   startIdx     — index of first row on the page (handy for serial numbers)
 *   controls     — a ready-to-render <TablePagination /> JSX block
 *   page, setPage, pageSize, setPageSize, totalCount, totalPages
 */
export const usePagination = (items, opts = {}) => {
  const {
    initialPageSize = 10,
    pageSizes = [10, 25, 50, 100],
    storageKey,           // optional — persist page-size in localStorage
    testIdPrefix = 'tbl',
  } = opts;

  const [pageSize, setPageSize] = useState(() => {
    if (storageKey) {
      const v = parseInt(localStorage.getItem(storageKey) || '', 10);
      if (pageSizes.includes(v)) return v;
    }
    return initialPageSize;
  });
  const [page, setPage] = useState(1);

  // Persist page-size if asked
  useEffect(() => {
    if (storageKey) localStorage.setItem(storageKey, String(pageSize));
  }, [pageSize, storageKey]);

  const totalCount = items.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  // Clamp page derivatively — handles "filters narrowed list" without a setState effect.
  const safePage = Math.min(Math.max(1, page), totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, totalCount);

  const paged = useMemo(
    () => items.slice(startIdx, endIdx),
    [items, startIdx, endIdx],
  );

  // When user changes page-size, jump back to page 1 immediately (synchronous via wrapper)
  const changePageSize = (sz) => { setPageSize(sz); setPage(1); };

  const controls = (
    <TablePagination
      page={safePage}
      totalPages={totalPages}
      pageSize={pageSize}
      pageSizes={pageSizes}
      totalCount={totalCount}
      startIdx={startIdx}
      endIdx={endIdx}
      onPageChange={setPage}
      onPageSizeChange={changePageSize}
      testIdPrefix={testIdPrefix}
    />
  );

  return {
    paged, controls,
    startIdx, endIdx,
    page: safePage, setPage,
    pageSize, setPageSize,
    totalCount, totalPages,
  };
};

export const TablePagination = ({
  page, totalPages, pageSize, pageSizes = [10, 25, 50, 100],
  totalCount, startIdx, endIdx,
  onPageChange, onPageSizeChange,
  testIdPrefix = 'tbl',
}) => {
  return (
    <div
      className="px-3 py-2.5 flex items-center justify-between gap-3 flex-wrap border-t bg-white"
      style={{ borderColor: 'var(--dx-border, #E4E4E7)' }}
      data-testid={`${testIdPrefix}-pagination`}
    >
      <div className="flex items-center gap-2 text-sm text-[#52525B]">
        <span>Show</span>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
          className="text-sm px-2 py-1.5 border rounded bg-white font-semibold"
          data-testid={`${testIdPrefix}-page-size`}
        >
          {pageSizes.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <span data-testid={`${testIdPrefix}-pagination-summary`}>
          {totalCount === 0
            ? 'Showing 0 results'
            : `Showing ${startIdx + 1} to ${endIdx} of ${totalCount} results`}
        </span>
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="px-2 py-1.5 text-sm border rounded bg-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
          data-testid={`${testIdPrefix}-page-prev`}
          aria-label="Previous page"
        >‹</button>
        {(() => {
          const pages = [];
          const add = (v) => pages.push(v);
          const windowSize = 1;
          for (let p = 1; p <= totalPages; p += 1) {
            if (p === 1 || p === totalPages || (p >= page - windowSize && p <= page + windowSize)) {
              add(p);
            } else if (pages[pages.length - 1] !== '…') {
              add('…');
            }
          }
          return pages.map((p, i) => p === '…' ? (
            <span key={`e${i}`} className="px-1.5 text-gray-400 text-sm">…</span>
          ) : (
            <button
              type="button"
              key={p}
              onClick={() => onPageChange(p)}
              className={`min-w-[32px] px-2 py-1.5 text-sm border rounded font-semibold ${p === page ? 'text-white' : 'bg-white hover:bg-gray-50'}`}
              style={p === page ? { background: 'var(--dx-primary, #1D9E75)', borderColor: 'var(--dx-primary, #1D9E75)' } : undefined}
              data-testid={`${testIdPrefix}-page-${p}`}
              aria-current={p === page ? 'page' : undefined}
            >{p}</button>
          ));
        })()}
        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          className="px-2 py-1.5 text-sm border rounded bg-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
          data-testid={`${testIdPrefix}-page-next`}
          aria-label="Next page"
        >›</button>
      </div>
    </div>
  );
};

export default TablePagination;
